import { checkStartup, installFavicon } from './startup';
import { handleResize, watchCanvasResize, type ResizeState } from './resize';
import { skipRenderWhenEmpty } from './viewportPipeline';
import { attachRuntimeAutomation } from './runtimeAutomation';
import { startFrameLoop } from './frameLoop';
import { sceneSeconds } from './sceneClock';
import { connectCitySession, exploreAlone, leaveSharedCityWhenSignedOut } from './citySession';
import { bindCityMenu } from './cityMenu';
import { revealCity } from './revealCity';
import { painterFrameFit } from './cameraRig';
import { createVisitorRuntime } from './visitorRuntime';
import { createCommunicationRuntime } from './communicationRuntime';
import { createEmoteRuntime } from './emoteRuntime';
import { createDiscoveryRuntime } from './discoveryRuntime';
import { createControlRuntimeEnv } from './controlEnvironment';
import { initializeWorld, type WorldInit } from './initializeWorld';
import { updateControlRuntime, createEmptyFrame } from './controlRuntime';
import { WARP_CULLING_RELEASED } from './warpCulling';
import {
  createUiState,
  reduceUi,
  settleOverlay,
  uiEffects,
  createHudState,
  type UiState,
  type UiEvent,
  type UiEffect,
} from './uiState';
import { createLoadingScreen } from '../ui/loadingScreen';
import { createAccountRuntime, type AccountRuntime } from './accountRuntime';
import { createErrorModal } from '../ui/errorModal';
import { createHud } from '../ui/hud';
import { createRuntimeTouch, updateRuntimeTouch } from './touchRuntime';
import { createHelpModal } from '../ui/helpModal';
import { createPaintingOverlay } from '../ui/paintingOverlay';
import { createPaintingMinimap } from '../ui/paintingMinimap';
import { createTourRuntime } from './tourRuntime';
import { createAmbientAudio } from '../audio/ambientAudio';
import { CityTime } from '../time/cityTime';
import { daylight, dayWindow } from '../time/daylight';
import { createCityLights } from './cityLights';
import { tourHandoff } from '../tours/tourHandoff';
import { QUALITY_TIERS } from '../render/quality';
import { createInitialControlState } from './initialControl';
import { createMultiplayerControlState } from './multiplayerSpawn';
import { createInputAdapter } from '../controls/inputAdapter';
import type { InputFrame, UiAction } from '../controls/types';
import { boatEye, boatRide } from '../controls/boat';
import { createTourBoat, type TourBoat } from '../tours/tourBoat';
import type { UrlOptions } from './types';
import type { FrameRect, LoadingScreen } from '../ui/types';
import type { Runtime } from './runtime';

const UI_EVENTS: Readonly<Record<UiAction, UiEvent>> = {
  help: 'toggleHelp',
  overlay: 'toggleOverlay',
  quality: 'cycleQuality',
  minimap: 'toggleMinimap',
  coords: 'toggleCoords',
};

export async function bootstrap(doc: Document, win: Window): Promise<void> {
  doc.documentElement.dataset.theme = 'dark';
  doc.addEventListener('contextmenu', (event) => {
    event.preventDefault();
  });
  doc.addEventListener('selectstart', (event) => {
    event.preventDefault();
  });
  installFavicon(doc);
  const errorModal = createErrorModal();
  const accounts = createAccountRuntime();
  const loadingScreen = createLoadingScreen(accounts.account, accounts.open);

  const options = checkStartup(win, errorModal);
  if (!options) return;

  const init = await initializeWorld(win, { loadingScreen, errorModal }, options);
  if (!init) return;

  const runtime = createRuntime(init, options, loadingScreen, accounts, () => {
    unbindMenu.open();
  });
  accounts.attach(runtime);
  leaveSharedCityWhenSignedOut(runtime, options);
  let started = false;
  const unbindMenu = bindCityMenu(
    doc,
    win,
    loadingScreen,
    () => started,
    (open) => {
      runtime.menuOpen = open;
      runtime.input?.setEnabled(!open && !runtime.ui.helpOpen);
    },
    () => runtime.helpModal.isOpen
  );
  win.addEventListener('pagehide', unbindMenu.dispose, { once: true });
  runtime.tours.mount(loadingScreen.tours);
  runtime.audio.mount(loadingScreen.controls);
  runtime.visitorView.mount();
  updateHud(runtime);
  /* Attached as soon as the renderer exists, well before Enter is clickable: the canvas can be
     hidden or unlaid-out for the whole build (the bug this fixes), and initializeWorld's own
     resize call has already run by now, so this is what retries once the canvas gets a real box,
     with no window resize event required. */
  runtime.stopResizeWatch = watchCanvasResize(init.renderer.domElement, win, () => {
    onCanvasResize(runtime);
  });

  loadingScreen.setReady(
    (mode, character, name, clothing) => {
      const resetPosition = !started || runtime.gameMode !== mode;
      runtime.character = character;
      runtime.name = name;
      runtime.clothing = clothing;
      if (mode === 'multi') {
        runtime.network?.dispose();
        runtime.network = null;
        runtime.gameMode = mode;
        runtime.time.lock();
        commitUi(runtime, { ...runtime.ui, overlay: 'hidden' });
        runtime.options = {
          seed: options.seed,
          quality: options.quality,
          still: false,
          automation: options.automation,
          view: { kind: 'spawn' },
          mode: 'walk',
        };
        runtime.env.env = { ...init.env, allowFlight: false };
        if (resetPosition)
          runtime.control.controlState = createMultiplayerControlState(runtime.env.env);
        runtime.tours.stop();
      } else exploreAlone(runtime, options);
      if (!started) {
        started = true;
        start(runtime, win);
      } else {
        connectCitySession(runtime);
      }
      revealCity(runtime, loadingScreen, win);
    },
    import.meta.env.VITE_MULTIPLAYER === 'true' && !options.still && options.seed === 1538,
    init.citizens
  );

  if (options.automation) attachRuntimeAutomation(win, runtime);
}

function createRuntime(
  init: WorldInit,
  options: UrlOptions,
  screen: LoadingScreen,
  accounts: AccountRuntime,
  openGateway: () => void
): Runtime {
  const time = new CityTime(Date.now, options.time);
  const runtime: Runtime = {
    gameMode: 'single',
    character: 'male',
    name: 'Jan Claesz de Dove',
    clothing: 'russet',
    visitorView: createVisitorRuntime(
      init,
      () => ({
        character: runtime.character,
        name: runtime.name,
        clothing: runtime.clothing,
        emote: runtime.emotes.current(),
      }),
      () => runtime.emotes.epochMs()
    ),
    communications: createCommunicationRuntime(init, () => runtime),
    emotes: createEmoteRuntime(() => runtime),
    account: accounts.account,
    discoveries: createDiscoveryRuntime(init, () => runtime, accounts.account, accounts.open),
    time,
    lights: createCityLights(init.world, () => runtime),
    audio: createAmbientAudio(init.data.layout, init.world.root, !options.automation),
    boat: ownBoat(init),
    init,
    options,
    env: createControlRuntimeEnv(init),
    hud: createHud(screen.controls, () => {
      dispatchUi(runtime, 'cycleQuality');
    }),
    helpModal: createHelpModal(
      () => {
        dispatchUi(runtime, 'dismissHelp');
      },
      () => runtime.gameMode === 'multi'
    ),
    overlay: createPaintingOverlay(),
    minimap: createPaintingMinimap(
      init.data.painterView,
      () => {
        dispatchUi(runtime, 'toggleHelp');
      },
      time
    ),
    tours: createTourRuntime(
      init,
      (pose, mode) => {
        runtime.control.controlState = {
          ...runtime.control.controlState,
          mode,
          pose,
          velocity: [0, 0, 0],
          transition: null,
          atPainterView: false,
        };
      },
      () => {
        runtime.input?.setEnabled(false);
        runtime.input?.setEnabled(!runtime.ui.helpOpen && !runtime.menuOpen);
        if (document.pointerLockElement) document.exitPointerLock();
      },
      () => {
        runtime.control.controlState = tourHandoff(runtime.control.controlState, init.env);
      },
      () => runtime.gameMode === 'multi' || runtime.ui.helpOpen || runtime.ui.overlay === 'visible',
      openGateway,
      () => {
        if (screen.isOpen) screen.enter('single');
      },
      () => {
        screen.resume();
      }
    ),
    control: {
      controlState: createInitialControlState(
        options,
        init.world.spawn,
        init.painterPose,
        init.env
      ),
      warpCulling: WARP_CULLING_RELEASED,
    },
    ui: createUiState(options.quality),
    input: null,
    touch: createRuntimeTouch(() => runtime, openGateway),
    stopResizeWatch: () => undefined,
    network: null,
    menuOpen: true,
  };
  return runtime;
}

function ownBoat(init: WorldInit): TourBoat {
  const boat = createTourBoat(init.data.palette, init.materials);
  init.scene.add(boat.root);
  return boat;
}

function start(rt: Runtime, win: Window): void {
  const { renderer, scene, camera, pipeline, world } = rt.init;
  rt.input = createInputAdapter(renderer.domElement, document, {
    mode: () => rt.control.controlState.mode,
    thirdPerson: () => rt.visitorView.behind,
    touch: rt.touch.input,
  });
  rt.minimap.mount();
  rt.minimap.setVisible(rt.ui.minimapVisible);
  if (rt.options.tour && rt.gameMode === 'single') rt.tours.start(rt.options.tour);
  updateHud(rt);
  connectCitySession(rt);
  rt.overlay.layout(overlayRect(rt));
  startFrameLoop(
    {
      renderer,
      scene,
      camera,
      pipeline: skipRenderWhenEmpty(pipeline, renderer.domElement),
      animated: world.animated,
      animationSeconds: (now) => sceneSeconds(rt.options.still, now / 1000),
      skyMesh: scene.getObjectByName('sky')!,
      onFrame: (dt) => {
        onFrame(rt, dt);
      },
    },
    rt.options.still
  );
  win.addEventListener('pagehide', () => {
    dispose(rt);
  });
}

function onFrame(rt: Runtime, dt: number): void {
  const poll = rt.options.still || !rt.input ? null : rt.input.poll();
  let requestPainterView = false;
  for (const action of poll?.ui ?? []) {
    requestPainterView = dispatchUi(rt, UI_EVENTS[action]) || requestPainterView;
  }
  const viewed = rt.visitorView.prepareInput(
    rt.tours.input(withPainterRequest(poll?.frame ?? createEmptyFrame(), requestPainterView)),
    rt.control.controlState.mode,
    dt
  );
  const frame = rt.communications.input(viewed, dt);
  const held =
    rt.menuOpen ||
    rt.ui.helpOpen ||
    rt.communications.isOpen ||
    rt.emotes.isOpen ||
    rt.discoveries.isOpen;
  updateRuntimeTouch(rt, held);
  const touring = rt.tours.update(frame, dt, held || document.hidden || rt.options.still);

  const result = updateControlRuntime(
    rt.control,
    { frame, dt },
    rt.env,
    held || touring,
    rt.options.still
  );
  rt.control = { controlState: result.controlState, warpCulling: result.warpCulling };
  const clock = rt.time.snapshot(),
    light = daylight(clock),
    seconds = sceneSeconds(rt.options.still);
  rt.visitorView.update(result.controlState, seconds, dt, light.night, rt.tours.state());
  rt.network?.update(light.night);
  rt.communications.update();
  rt.discoveries.update();
  const day = dayWindow(clock);
  for (const animation of rt.init.world.animated)
    animation.setTimeOfDay?.(clock.minutes, light.activity, day);
  rt.init.materials.setSunDirection(light.sun);
  rt.init.pipeline.setDaylight(light);
  rt.init.pipeline.setNight({
    lights: light.night > 0 ? rt.lights.list() : [],
    night: light.night,
    burning: light.burning,
    timeSeconds: seconds,
  });
  const { boat, mode: controlMode, pose } = rt.control.controlState;
  if (boat) {
    const swellAt = rt.init.env.swellAt;
    const ride = swellAt ? boatRide(boat, swellAt) : undefined;
    rt.boat.update({ ...pose, position: boatEye(boat) }, boat.bearing, true, ride);
  }
  const own = controlMode === 'walk' || controlMode === 'boat' ? controlMode : 'fly';
  rt.audio.update(pose, rt.tours.kind() ?? own, clock, light.activity);

  commitUi(rt, settleOverlay(rt.ui, rt.control.controlState));
  if (!rt.options.still) updateHud(rt);
}

function withPainterRequest(frame: InputFrame, requested: boolean): InputFrame {
  return requested ? { ...frame, actions: [...frame.actions, 'painterView'] } : frame;
}

function dispatchUi(rt: Runtime, event: UiEvent): boolean {
  if (rt.gameMode === 'multi' && event === 'toggleOverlay') return false;
  const step = reduceUi(rt.ui, event, rt.control.controlState);
  commitUi(rt, step.state);
  updateHud(rt);
  return step.requestPainterView;
}

function commitUi(rt: Runtime, next: UiState): void {
  const effects = uiEffects(rt.ui, next);
  rt.ui = next;
  for (const effect of effects) applyUiEffect(rt, effect);
}

function applyUiEffect(rt: Runtime, effect: UiEffect): void {
  switch (effect.kind) {
    case 'help':
      if (effect.open) rt.helpModal.open();
      else rt.helpModal.close();
      return;
    case 'input':
      rt.input?.setEnabled(effect.enabled && !rt.menuOpen && !rt.communications.isOpen);
      return;
    case 'overlay':
      if (effect.visible) rt.overlay.layout(overlayRect(rt));
      rt.overlay.setVisible(effect.visible);
      return;
    case 'quality':
      rt.init.pipeline.setQuality(QUALITY_TIERS[effect.tier]);
      resize(rt);
      return;
    case 'minimap':
      rt.minimap.setVisible(effect.visible);
      return;
  }
}

function updateHud(rt: Runtime): void {
  const pointerLock = rt.input?.pointerLockStatus ?? 'unlocked';
  rt.hud.update(createHudState(rt.control.controlState, rt.ui, pointerLock));
  rt.minimap.update(rt.control.controlState.pose);
}

function overlayRect(rt: Runtime): FrameRect {
  const canvas = rt.init.renderer.domElement;
  return painterFrameFit(canvas.clientWidth, canvas.clientHeight, rt.init.data.painterView).rect;
}

function resize(rt: Runtime): ResizeState | null {
  const { renderer, camera, pipeline, materials } = rt.init;
  return handleResize(renderer, camera, pipeline, materials, QUALITY_TIERS[rt.ui.tier]);
}

/* WHY skip the overlay layout on a null result: overlayRect divides by the canvas's own height to
   fit the painter frame, so laying it out against a canvas that just measured 0 would work
   through NaNs to reach the same 0-sized rect handleResize already decided to skip. */
function onCanvasResize(rt: Runtime): void {
  if (resize(rt)) rt.overlay.layout(overlayRect(rt));
  rt.minimap.resize();
}

function dispose(rt: Runtime): void {
  rt.touch.dispose();
  rt.emotes.dispose();
  rt.discoveries.dispose();
  rt.communications.dispose();
  rt.visitorView.dispose();
  rt.network?.dispose();
  rt.audio.dispose();
  rt.stopResizeWatch();
  rt.input?.dispose();
  rt.hud.dispose();
  rt.tours.dispose();
  rt.minimap.dispose();
  rt.boat.dispose();
  rt.overlay.setVisible(false);
  rt.init.world.dispose();
  rt.init.materials.dispose();
  rt.init.pipeline.dispose();
  rt.init.renderer.dispose();
}
